import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Briefcase, Loader2, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MagicLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('Invalid login link. Please request a new one.');
      return;
    }

    verifyMagicLink(token);
  }, [searchParams]);

  const verifyMagicLink = async (token) => {
    try {
      const response = await axios.post(`${API}/auth/magic-link/verify`, { token });
      
      // Establish session from the JWT issued by the magic-link endpoint
      loginWithToken(response.data.token, response.data.user, response.data.must_change_password);
      
      setStatus('success');
      
      // Redirect after a brief moment
      setTimeout(() => {
        if (response.data.must_change_password) {
          navigate('/set-new-password');
        } else {
          navigate('/dashboard');
        }
      }, 1500);
      
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to verify login link. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-slate-900" />
            </div>
            <span className="text-3xl font-bold text-white font-['Barlow_Condensed']">FieldReady</span>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-8">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Verifying Login Link</h2>
              <p className="text-slate-400">Please wait while we log you in...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Login Successful!</h2>
              <p className="text-slate-400">Redirecting to your dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Login Failed</h2>
              <p className="text-slate-400 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-6 py-2 rounded-lg transition-colors"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
